import Config from "ips/ac/config/Config";
import HttpUtil from "ips/ac/util/HttpUtil";
import Shell from "sap/m/Shell";
import ComponentContainer from "sap/ui/core/ComponentContainer";
import Component from "sap/ui/core/Component";
import Core from "sap/ui/core/Core";
import Mobile from "sap/ui/util/Mobile";
import UIComponent from "sap/ui/core/UIComponent";
import UserService from "ips/ac/data/user/service/UserService";
import { SubscriptionType } from "ips/ac/model/api/WinDoFlow.api.types";
import MessageBox from "sap/m/MessageBox";
import UserClassObj from "ips/ac/util/classes/user/User";
import SplashScreen from "ips/ac/util/SplashScreen";
import ResourceBundle from "sap/base/i18n/ResourceBundle";

export default class LoginService {
  private static instance: LoginService;
  private config: Config;
  private bAppLoaded: boolean = false;

  constructor() {
    this.config = new Config();
  }

  public async checkLogin() {
    const result = await HttpUtil.get(this.config.getValue("PublicRestUrl") + "/Login");

    if (!result.userIsLoggedIn) {
      const config = this.config.getDevelopmentConfig();
      if (config.apiToken) {
        await this.loginWithToken(config.apiToken);
      } else {
        this.redirectToLogin(true);
      }
    }

    if (!this.bAppLoaded) {
      const user = await UserService.loadCurrentUser();
      if (!(await this.hasRequiredSubscription(user))) {
        return;
      }
      await this.loadApp();
    }
  }

  private async hasRequiredSubscription(user: UserClassObj): Promise<boolean> {
    const client = user.getClient();
    if (client.hasSubscription(SubscriptionType.WIN_DO_WORKS)
      || client.hasSubscription(SubscriptionType.WIN_DO_WORKS_LIGHT)) {
      return true;
    }

    SplashScreen.remove();
    const bundle = await ResourceBundle.create({ url: sap.ui.require.toUrl("ips/ac/i18n/i18n.properties") });
    MessageBox.error(bundle.getText("SubscriptionRequiredText"), {
      onClose: () => this.redirectToLogin(),
    });
    return false;
  }

  private async loadApp() {
    await Core.ready();
    // early set icons to be recognized in ipad
    var sIconPath = sap.ui.require.toUrl("ips.ac.mime.icon");
    Mobile.setIcons({
      "favicon": sIconPath + "icon.png",
      "phone": sIconPath + "icon-57.png",
      "phone@2": sIconPath + "icon-57-2x.png",
      "tablet": sIconPath + "icon-72.png",
      "tablet@2": sIconPath + "icon-72-2x.png",
      "precomposed": false, // whether the home icons already have some glare effect (for iOS)
    });

    const oComponent = await Component.create({
      id: "windoflow_component",
      name: "ips.ac",
      settings: {
        // enable fakeLRep connector
        componentData: { startupParameters: { ...URLSearchParams, enableFakeLRepConnector: true } },
      },
    });

    const oCompContainer = new ComponentContainer("ipscockpit", { height: "100%" });
    oCompContainer.setComponent(oComponent as UIComponent);

    new Shell({
      id: "AppShell",
      app: oCompContainer,
      showLogout: false,
      appWidthLimited: false,
      logo: "./mime/icon/icon.png",
    }).placeAt("root");

    this.bAppLoaded = true;
  }

  public redirectToLogin(remember: boolean = true) {
    const selfUrl = (remember) ? btoa(self.location.href) : "";
    localStorage.setItem("removeCookie", "true");
    self.location.href = this.config.getValue("LoginUrl") + "#/login/Default/" + selfUrl;
  }

  public logoutUser() {
    HttpUtil.get(this.config.getRestUrl() + "/logout").then(() => this.redirectToLogin(false));
  }

  private async loginWithToken(token: string) {
    await HttpUtil.post("/IpsCore/public/service/login-token", null, { token: token });
  }

  public static getInstance() {
    if (!this.instance) {
      this.instance = new LoginService();
    }

    return this.instance;
  }
}

